import React from 'react';

const SIGNS = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
               "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];

const CONTRIBUTORS = ['Saturn', 'Jupiter', 'Mars', 'Sun', 'Venus', 'Mercury', 'Moon', 'Lagna'];

/**
 * Component to display Ashtakavarga BAV matrices with 0/1 bindus
 */
const AshtakavargaMatrixDisplay = ({ ashtakavargaData, d1Data }) => {
  if (!ashtakavargaData || !ashtakavargaData['13.5_detailed_bav_matrices']) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
        <p className="text-yellow-800">Detailed Ashtakavarga matrices not available</p>
      </div>
    );
  }

  const detailedBAVs = ashtakavargaData['13.5_detailed_bav_matrices'];
  const savMatrix = ashtakavargaData['13.6_sav_detailed_matrix'];
  
  const planets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Lagna'];

  /**
   * Get planet's sign index from D1 data
   */
  const getPlanetPosition = (planetName) => {
    if (!d1Data) {
      return 0; // Fallback to Aries
    }
    
    // Special handling for Lagna
    if (planetName === 'Lagna') {
      if (d1Data.lagna && d1Data.lagna.Sign) {
        const idx = SIGNS.indexOf(d1Data.lagna.Sign);
        if (idx >= 0) return idx;
      }
    }
    
    // Get planets array
    const planets = d1Data.planets || d1Data.all_rows || [];
    if (!Array.isArray(planets)) {
      return 0;
    }
    
    // Find the planet in D1 data
    const planetData = planets.find(p => p.Planet === planetName);
    if (planetData && planetData.Sign) {
      const idx = SIGNS.indexOf(planetData.Sign);
      if (idx >= 0) return idx;
    }
    
    // Fallback to Aries (index 0)
    return 0;
  };

  /**
   * Render a single BAV matrix with columns starting from planet's position
   */
  const renderBAVMatrix = (planetName) => {
    const bavData = detailedBAVs[planetName];
    if (!bavData || !bavData.matrix) return null;

    const matrix = bavData.matrix;
    const rowTotals = bavData.row_totals;
    const columnTotals = bavData.column_totals;
    const grandTotal = bavData.grand_total;
    
    // Get planet position to rotate columns
    const planetPos = getPlanetPosition(planetName);
    const planetSign = SIGNS[planetPos];
    
    // Create rotated column order: start from planet's sign
    const rotatedColIndices = [];
    for (let i = 0; i < 12; i++) {
      rotatedColIndices.push((planetPos + i) % 12);
    }

    return (
      <div key={planetName} className="mb-8 bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
          <h3 className="text-2xl font-bold text-white">{planetName}'s Bhinnashtakavarga</h3>
          <p className="text-purple-100 text-sm mt-1">Columns start from {planetSign} ({planetName}'s position)</p>
        </div>
        
        <div className="overflow-x-auto p-6">
          <table className="w-full border-collapse">
            <thead>
              {/* First header row - House numbers (1-12) */}
              <tr className="bg-gradient-to-r from-purple-100 to-indigo-100">
                <th className="border border-purple-300 px-3 py-2 text-sm font-semibold text-gray-700">
                  Contributor
                </th>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((houseNum) => (
                  <th key={houseNum} className="border border-purple-300 px-2 py-2 text-xs font-medium text-gray-600">
                    {houseNum}
                  </th>
                ))}
                <th className="border border-purple-300 px-3 py-2 text-sm font-semibold text-gray-700 bg-purple-200">
                  Total
                </th>
              </tr>
              {/* Second header row - Sign names (rotated) */}
              <tr className="bg-purple-50">
                <th className="border border-purple-300 px-3 py-2 text-xs text-gray-500">
                  {planetName} at {planetSign}
                </th>
                {rotatedColIndices.map((signIdx) => (
                  <th key={signIdx} className="border border-purple-300 px-1 py-1 text-xs font-medium text-gray-600">
                    {SIGNS[signIdx].substring(0, 3)}
                  </th>
                ))}
                <th className="border border-purple-300"></th>
              </tr>
            </thead>
            <tbody>
              {CONTRIBUTORS.map((contributor, rowIdx) => (
                <tr key={contributor} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 px-3 py-2 font-semibold text-gray-700">
                    {contributor}
                  </td>
                  {rotatedColIndices.map((signIdx) => {
                    const bindu = matrix[rowIdx][signIdx];
                    return (
                      <td
                        key={signIdx}
                        className={`border border-gray-300 px-2 py-2 text-center font-mono text-sm ${
                          bindu === 1 
                            ? 'bg-green-100 text-green-800 font-bold' 
                            : 'bg-red-50 text-gray-400'
                        }`}
                      >
                        {bindu}
                      </td>
                    );
                  })}
                  <td className="border border-gray-300 px-3 py-2 text-center font-bold bg-yellow-100 text-gray-800">
                    {rowTotals[rowIdx]}
                  </td>
                </tr>
              ))}
              {/* Column totals row */}
              <tr className="bg-gradient-to-r from-yellow-100 to-amber-100 font-bold">
                <td className="border border-gray-300 px-3 py-2 text-gray-700">
                  TOTALS
                </td>
                {rotatedColIndices.map((signIdx) => (
                  <td key={signIdx} className="border border-gray-300 px-2 py-2 text-center text-gray-800">
                    {columnTotals[signIdx]}
                  </td>
                ))}
                <td className="border border-gray-300 px-3 py-2 text-center bg-yellow-200 text-gray-900 text-lg">
                  {grandTotal}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            <span className="font-semibold">Grand Total:</span> {grandTotal} bindus
            <span className="ml-4 text-green-600">
              {grandTotal === {Sun:48, Moon:49, Mars:39, Mercury:54, Jupiter:56, Venus:52, Saturn:39, Lagna:49}[planetName] && '✓ Verified'}
            </span>
          </p>
        </div>
      </div>
    );
  };

  /**
   * Render Lagna BAV matrix
   */
  const renderLagnaBAV = () => {
    const lagnaBAV = detailedBAVs?.['Lagna'];
    if (!lagnaBAV || !lagnaBAV.bav) return null;

    const bavArray = lagnaBAV.bav;
    const total = lagnaBAV.total;

    return (
      <div className="mb-8 bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4">
          <h3 className="text-2xl font-bold text-white">Lagna Ashtakavarga</h3>
          <p className="text-cyan-100 text-sm mt-1">Ascendant's benefic points</p>
        </div>
        
        <div className="overflow-x-auto p-6">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-cyan-100 to-blue-100">
                <th className="border border-cyan-300 px-3 py-2 text-sm font-semibold text-gray-700">
                  Contributor
                </th>
                {SIGNS.map((_, idx) => (
                  <th key={idx} className="border border-cyan-300 px-2 py-2 text-xs font-medium text-gray-600">
                    {idx}
                  </th>
                ))}
                <th className="border border-cyan-300 px-3 py-2 text-sm font-semibold text-gray-700 bg-cyan-200">
                  Total
                </th>
              </tr>
              <tr className="bg-cyan-50">
                <th className="border border-cyan-300 px-3 py-2 text-xs text-gray-500">
                  Position
                </th>
                {SIGNS.map((sign, idx) => (
                  <th key={idx} className="border border-cyan-300 px-1 py-1 text-xs font-medium text-gray-600">
                    {sign.substring(0, 3)}
                  </th>
                ))}
                <th className="border border-cyan-300"></th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white">
                <td className="border border-gray-300 px-3 py-2 font-semibold text-gray-700">
                  Lagna
                </td>
                {bavArray.map((bindu, idx) => (
                  <td
                    key={idx}
                    className={`border border-gray-300 px-2 py-2 text-center font-mono text-sm ${
                      bindu === 1 
                        ? 'bg-green-100 text-green-800 font-bold' 
                        : 'bg-red-50 text-gray-400'
                    }`}
                  >
                    {bindu}
                  </td>
                ))}
                <td className="border border-gray-300 px-3 py-2 text-center font-bold bg-yellow-100 text-gray-800 text-lg">
                  {total}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            <span className="font-semibold">Total:</span> {total} bindus
          </p>
        </div>
      </div>
    );
  };

  /**
   * Render Sarvashtakavarga matrix
   */
  const renderSAVMatrix = () => {
    if (!savMatrix || !savMatrix.matrix) return null;

    const matrix = savMatrix.matrix;
    const columnTotals = savMatrix.column_totals;
    const grandTotal = savMatrix.grand_total;

    return (
      <div className="mb-8 bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-orange-600 to-red-600 px-6 py-4">
          <h3 className="text-2xl font-bold text-white">Sarvashtakavarga (SAV)</h3>
          <p className="text-orange-100 text-sm mt-1">Combined total of all 7 planets</p>
        </div>
        
        <div className="overflow-x-auto p-6">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-orange-100 to-red-100">
                <th className="border border-orange-300 px-3 py-2 text-sm font-semibold text-gray-700">
                  Planet
                </th>
                {SIGNS.map((_, idx) => (
                  <th key={idx} className="border border-orange-300 px-2 py-2 text-xs font-medium text-gray-600">
                    {idx}
                  </th>
                ))}
                <th className="border border-orange-300 px-3 py-2 text-sm font-semibold text-gray-700 bg-orange-200">
                  Total
                </th>
              </tr>
              <tr className="bg-orange-50">
                <th className="border border-orange-300 px-3 py-2 text-xs text-gray-500">
                  Sign
                </th>
                {SIGNS.map((sign, idx) => (
                  <th key={idx} className="border border-orange-300 px-1 py-1 text-xs font-medium text-gray-600">
                    {sign.substring(0, 3)}
                  </th>
                ))}
                <th className="border border-orange-300"></th>
              </tr>
            </thead>
            <tbody>
              {planets.map((planet, rowIdx) => {
                const planetRow = detailedBAVs[planet]?.column_totals || [];
                const rowTotal = detailedBAVs[planet]?.grand_total || 0;
                
                return (
                  <tr key={planet} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-3 py-2 font-semibold text-gray-700">
                      {planet}
                    </td>
                    {planetRow.map((bindu, colIdx) => (
                      <td
                        key={colIdx}
                        className="border border-gray-300 px-2 py-2 text-center font-mono text-sm text-gray-700"
                      >
                        {bindu}
                      </td>
                    ))}
                    <td className="border border-gray-300 px-3 py-2 text-center font-bold bg-yellow-100 text-gray-800">
                      {rowTotal}
                    </td>
                  </tr>
                );
              })}
              {/* SAV Totals row */}
              <tr className="bg-gradient-to-r from-orange-200 to-red-200 font-bold text-lg">
                <td className="border border-gray-300 px-3 py-2 text-gray-800">
                  SAV TOTALS
                </td>
                {columnTotals.map((total, idx) => (
                  <td
                    key={idx}
                    className={`border border-gray-300 px-2 py-2 text-center text-gray-900 ${
                      total >= 30 ? 'bg-green-200' : total >= 25 ? 'bg-yellow-200' : 'bg-red-200'
                    }`}
                  >
                    {total}
                  </td>
                ))}
                <td className="border border-gray-300 px-3 py-2 text-center bg-orange-300 text-gray-900 text-xl">
                  {grandTotal}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            <span className="font-semibold">Grand SAV Total:</span> {grandTotal} bindus
            <span className={`ml-4 font-semibold ${grandTotal === 337 ? 'text-green-600' : 'text-red-600'}`}>
              {grandTotal === 337 ? '✓ Verified (Classical Standard)' : `✗ Expected 337, got ${grandTotal}`}
            </span>
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Color coding: Green (≥30 bindus - Strong), Yellow (25-29 - Moderate), Red (&lt;25 - Weak)
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-indigo-700 text-white rounded-xl p-6 shadow-xl">
        <h2 className="text-3xl font-bold mb-2">Ashtakavarga System - Complete Matrices</h2>
        <p className="text-purple-100">
          Detailed 0/1 bindu matrices for all planets showing classical Parashara calculations
        </p>
      </div>

      {/* All Planet BAV Matrices */}
      <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-purple-300 pb-2">
          Individual Planet Bhinnashtakavarga Matrices
        </h3>
        {planets.map(planet => renderBAVMatrix(planet))}
      </div>

      {/* Lagna is now included in the main planet loop above */}

      {/* Sarvashtakavarga */}
      <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-300 pb-2">
          Sarvashtakavarga (Combined Total)
        </h3>
        {renderSAVMatrix()}
      </div>

      {/* Legend */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-6 shadow-md">
        <h4 className="font-bold text-lg text-gray-800 mb-3">Understanding the Matrices</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div>
            <p className="font-semibold mb-2">Matrix Structure:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><span className="font-semibold">Rows:</span> 8 contributors (7 planets + Lagna)</li>
              <li><span className="font-semibold">Columns:</span> 12 zodiac signs (Aries to Pisces)</li>
              <li><span className="font-semibold">Values:</span> 0 (no bindu) or 1 (bindu present)</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold mb-2">Reading the Data:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><span className="font-semibold">Column totals:</span> Bindus each sign receives</li>
              <li><span className="font-semibold">Row totals:</span> Bindus each contributor gives</li>
              <li><span className="font-semibold">Grand total:</span> Total bindus for that planet's BAV</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AshtakavargaMatrixDisplay;
